import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { getErrorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";
import { partyLabel } from "@/lib/format";

const num = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const CAT = { STOCK: "Stock", NON_STOCK: "Non-stock", SERVICE: "Service" };

export default function ItemTracker() {
  const pf = useProjectFilter();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ category: "NON_STOCK_SERVICE", vendor_id: "all", date_from: "", date_to: "" });
  const [vendors, setVendors] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});
  const [search, setSearch] = useState("");

  const params = () => {
    const p = {};
    if (pf.value !== "all" && pf.value !== "none") p.project_id = pf.value;
    if (filters.vendor_id !== "all") p.vendor_id = filters.vendor_id;
    if (!["ALL", "NON_STOCK_SERVICE"].includes(filters.category)) p.category = filters.category;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/procurement/item-tracker", { params: params() });
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pf.value, filters.category, filters.vendor_id]);
  useEffect(() => { api.get("/procurement/vendors").then(({ data: v }) => setVendors(v || [])).catch(() => {}); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/procurement/item-tracker/export", { params: params(), responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = "item_tracker.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const q = search.trim().toLowerCase();
  const rows = (data?.rows || [])
    .filter((r) => filters.category !== "NON_STOCK_SERVICE" || r.category !== "STOCK")
    .filter((r) => pf.value !== "none" || !r.project_id)
    .filter((r) => !q || [r.item_code, r.item_name, r.vendors, r.project].some((t) => String(t || "").toLowerCase().includes(q)));
  const sum = (k) => rows.reduce((s, r) => s + Number(r[k] || 0), 0);

  return (
    <div className="space-y-5" data-testid="item-tracker-page">
      <PageHeader
        title="Procurement Tracker"
        description="Every item and service, per project: ordered → received / certified → billed → paid. Goods come from POs and GRNs; services from Work Orders and certified JMRs. Click a row to see the documents."
        action={<div className="flex flex-wrap items-center gap-2">
          <ProjectFilter filter={pf} />
          <Button variant="outline" onClick={exportCsv}><Download className="h-4 w-4" /> Excel (CSV)</Button>
        </div>}
      />
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>Category</Label>
          <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
            <SelectTrigger className="mt-1.5 w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="NON_STOCK_SERVICE">Non-stock + Service</SelectItem>
              <SelectItem value="NON_STOCK">Non-stock only</SelectItem>
              <SelectItem value="SERVICE">Service only</SelectItem>
              <SelectItem value="STOCK">Stock only</SelectItem>
              <SelectItem value="ALL">Everything</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Vendor</Label>
          <Select value={filters.vendor_id} onValueChange={(v) => setFilters({ ...filters, vendor_id: v })}>
            <SelectTrigger className="mt-1.5 w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All vendors</SelectItem>
              {vendors.map((v) => <SelectItem key={v.id} value={v.id}>{partyLabel(v)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Orders from</Label><Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1.5" /></div>
        <div><Label>to</Label><Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1.5" /></div>
        <Button onClick={load} disabled={loading}><RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} /> Show</Button>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search item, vendor, project" className="ml-auto w-64" />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[["Ordered", sum("ordered_value")], ["Billed", sum("billed_value")], ["Paid", sum("paid_value")], ["Billed, not paid", sum("unpaid_value")]].map(([k, v]) => (
          <div key={k} className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{k}</p>
            <p className="mt-2 font-mono text-lg font-bold text-foreground">{inr(v)}</p>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        {!loading && rows.length === 0 ? <EmptyState title="Nothing to show" description="No approved POs or work orders match these filters." /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-6" /><TableHead>Item</TableHead><TableHead>Project</TableHead><TableHead className="text-right">Ordered</TableHead>
              <TableHead className="text-right">Received / done</TableHead><TableHead className="text-right">Billed</TableHead><TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Pending to receive / do</TableHead><TableHead className="text-right">Pending to bill</TableHead><TableHead className="text-right">Avg rate</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <Fragment key={r.key}>
                  <TableRow className="cursor-pointer" onClick={() => setOpen({ ...open, [r.key]: !open[r.key] })} data-testid={`tracker-row-${r.item_code || r.item_name}`}>
                    <TableCell>{open[r.key] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</TableCell>
                    <TableCell>
                      <div className="text-foreground">{r.item_name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {r.item_code || "typed, not from catalogue"} · <span className={r.category === "SERVICE" ? "text-violet-600" : r.category === "NON_STOCK" ? "text-amber-600" : "text-sky-600"}>{CAT[r.category]}</span>{r.vendors ? ` · ${r.vendors}` : ""}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{r.project}</TableCell>
                    <TableCell className="text-right font-mono text-xs"><div>{num(r.ordered_qty)} {r.unit}</div><div className="text-muted-foreground">{inr(r.ordered_value)}</div></TableCell>
                    <TableCell className="text-right font-mono text-xs">{num(r.done_qty)}</TableCell>
                    <TableCell className="text-right font-mono text-xs"><div>{num(r.billed_qty)}</div><div className="text-muted-foreground">{inr(r.billed_value)}</div></TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(r.paid_value)}{Number(r.unpaid_value) > 1 && <div className="text-amber-600">due {inr(r.unpaid_value)}</div>}</TableCell>
                    <TableCell className={`text-right font-mono text-xs ${Number(r.pending_done) > 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>{num(r.pending_done)}</TableCell>
                    <TableCell className={`text-right font-mono text-xs ${Number(r.pending_bill) > 0 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"}`}>{num(r.pending_bill)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{inr(r.avg_rate)}</TableCell>
                  </TableRow>
                  {open[r.key] && (
                    <TableRow className="bg-muted/30">
                      <TableCell />
                      <TableCell colSpan={9}>
                        <div className="flex flex-wrap gap-2 py-1">
                          {r.docs.map((d, i) => (
                            <button key={i} type="button" onClick={() => navigate(d.link)} className="rounded-md border border-border bg-card px-2 py-1 text-left text-[11px] hover:border-foreground/30">
                              <span className="font-semibold text-foreground">{d.type}</span> <span className="font-mono">{d.number}</span>
                              <span className="text-muted-foreground"> · {d.date} · qty {num(d.qty)} · {inr(d.amount)}</span>
                            </button>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
